import * as Sentry from '@sentry/vue';
import {
  isThirdPartyWebviewNoise,
  isOfflineNetworkError,
  isNavigationAbortNoise,
} from './sentry-noise';

// Front-end error tracking (@sentry/vue, errors only). The DSN / environment /
// release come from <meta> tags that Rails only emits in production with
// ACTIVE_SENTRY=true (see app/views/layouts/_sentry_meta.html.erb), so
// initSentry() is a no-op in local/dev.
//
// Two-step init:
// - initSentry() installs window.onerror / unhandledrejection and breadcrumbs,
//   WITHOUT the Vue integration — on pages that never mount a Vue app, the
//   SDK's default vueIntegration would print an un-gated "Misconfigured SDK"
//   console.warn in production. Entrypoints call it via sentry-boot (their
//   FIRST import) so it runs before any other module evaluates.
// - attachVueAppToSentry(app) adds vueIntegration({ app }) once the Vue app
//   exists (hooks app.config.errorHandler: component render/watcher crashes,
//   the exact class of bug the @vue/compat migration produced). Must run
//   before app.mount().
//
// Privacy: Devise pages run this bundle, and their URLs carry live
// reset_password_token / confirmation_token values — account-takeover
// credentials that must never reach a third-party service. beforeSend /
// beforeBreadcrumb strip the query string and fragment from every URL FIELD
// the SDK reports (request.url, Referer, breadcrumb urls, stack-frame
// filenames — window.onerror uses the document URL as the frame filename for
// inline-script and cross-origin errors) and from URLs embedded in exception
// messages. NOT covered: tokens in the URL PATH (/kyc/from_qr/:user_token
// only ever 302-redirects, so no JS executes on that URL). Vue component
// props are never attached (attachProps: false): they can carry counterparty
// PII and transfer amounts.

const sentryMeta = (name) => document.querySelector(`meta[name="${name}"]`)?.getAttribute('content') || null;

const stripUrl = (value) => (typeof value === 'string' ? value.split('?')[0].split('#')[0] : value);

// "failed to fetch https://x.com/a?token=..." inside an error message.
const stripEmbeddedUrls = (text) => text.replace(/(https?:\/\/[^\s'"<>]+?)[?#][^\s'"<>]*/g, '$1');

function scrubEvent(event) {
  if (event.request) {
    if (event.request.url) event.request.url = stripUrl(event.request.url);
    if (event.request.headers && event.request.headers.Referer) {
      event.request.headers.Referer = stripUrl(event.request.headers.Referer);
    }
  }
  (event.exception?.values || []).forEach((exception) => {
    if (typeof exception.value === 'string') exception.value = stripEmbeddedUrls(exception.value);
    (exception.stacktrace?.frames || []).forEach((frame) => {
      // Asset URLs carry no meaningful query, so stripping every frame is safe.
      if (frame.filename) frame.filename = stripUrl(frame.filename);
      if (frame.abs_path) frame.abs_path = stripUrl(frame.abs_path);
    });
  });
  return event;
}

// navigation breadcrumbs use data.from/data.to; fetch/xhr use data.url.
function scrubBreadcrumb(breadcrumb) {
  if (breadcrumb.data) {
    ['url', 'from', 'to'].forEach((key) => {
      if (typeof breadcrumb.data[key] === 'string') breadcrumb.data[key] = stripUrl(breadcrumb.data[key]);
    });
  }
  return breadcrumb;
}

export function initSentry() {
  const dsn = sentryMeta('sentry-dsn');
  if (!dsn) return; // Rails withheld the DSN (dev, or ACTIVE_SENTRY off)

  Sentry.init({
    dsn,
    environment: sentryMeta('sentry-environment') || 'production',
    // The meta is omitted (never a placeholder) when HEROKU_SLUG_COMMIT is
    // absent — a fake release like "unknown" would poison Sentry's release /
    // regression tracking, which `sentry issue resolve --in @next` relies on.
    release: sentryMeta('sentry-release') || undefined,
    // Errors only. Deliberately NO tracesSampleRate / replays keys at all:
    // the SDK treats the mere presence of tracesSampleRate (even 0) as
    // "tracing enabled" and vueIntegration would then install lifecycle
    // mixins on every component — pure overhead when nothing is sampled.
    integrations: (defaults) => defaults.filter((integration) => integration.name !== 'Vue'),
    // Descarte de ruido no-accionable ANTES del scrub. Va en beforeSend
    // inspeccionando STACK FRAMES — no en ignoreErrors, que solo compara el
    // mensaje: la primera versión usó ignoreErrors y no filtró nada (46
    // eventos post-"filtro" en ARCADI-E7, cuyo patrón vive en los frames del
    // script inyectado por Meta, no en el mensaje). La lógica y sus tests
    // contra los eventos reales viven en sentry-noise.js.
    beforeSend: (event) => {
      if (isThirdPartyWebviewNoise(event)) return null;
      if (isOfflineNetworkError(event)) return null;
      if (isNavigationAbortNoise(event)) return null;
      return scrubEvent(event);
    },
    beforeBreadcrumb: scrubBreadcrumb,
  });
}

export function attachVueAppToSentry(app) {
  if (!Sentry.isInitialized()) return; // no DSN → Sentry is off on this page

  // attachProps: false — the crashing component's props can carry counterparty
  // PII / financial data (e.g. internal-contact-card's `contact` object); the
  // component name in the stacktrace is enough to debug.
  Sentry.addIntegration(Sentry.vueIntegration({ app, attachProps: false }));
}
